import type { paths } from '@cou/contracts';

/*
 * The Contests API client (API-09, admin-ui.md). CAE-282 has landed the real DB-backed
 * routes — list/detail/finalise all read/write the database, not CAE-241 stubs. Injectable at
 * the screen for tests, mirroring apps/admin/src/config/config-client.ts.
 */

export type AdminContestWithEntryCount =
  paths['/api/admin/contests']['get']['responses'][200]['content']['application/json']['contests'][number];

type ListContestsBody =
  paths['/api/admin/contests']['get']['responses'][200]['content']['application/json'];
type FinaliseBody =
  paths['/api/admin/contests/{id}/finalise']['post']['responses'][200]['content']['application/json'];
type RevertBody =
  paths['/api/admin/contests/{id}/revert']['post']['responses'][200]['content']['application/json'];
type CreateContestBody =
  paths['/api/admin/contests']['post']['requestBody']['content']['application/json'];
type CreateContestResponseBody =
  paths['/api/admin/contests']['post']['responses'][200]['content']['application/json'];
type UpdateContestBody =
  paths['/api/admin/contests/{id}']['put']['requestBody']['content']['application/json'];
type UpdateContestResponseBody =
  paths['/api/admin/contests/{id}']['put']['responses'][200]['content']['application/json'];

export interface ApiFailure {
  readonly ok: false;
  readonly status: number;
  readonly code: string | undefined;
  readonly message?: string;
  /** Present on 409 GL-16 guard failures (half_point_line / suspended_props). Untyped by the
   * generated ErrorResponse schema, which only declares code/message — read defensively. */
  readonly details?: Record<string, unknown>;
}

export type ListContestsResult = ({ ok: true } & ListContestsBody) | ApiFailure;
export type GetContestResult = { ok: true; contest: AdminContestWithEntryCount } | ApiFailure;
export type FinaliseContestResult = ({ ok: true } & FinaliseBody) | ApiFailure;
export type RevertContestResult = { ok: true; contest: RevertBody } | ApiFailure;
export type CreateContestInput = CreateContestBody;
export type CreateContestResult = { ok: true; contest: CreateContestResponseBody } | ApiFailure;
export type UpdateContestPatch = UpdateContestBody;
export type UpdateContestResult = ({ ok: true } & UpdateContestResponseBody) | ApiFailure;

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

async function failure(response: Response): Promise<ApiFailure> {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string; details?: Record<string, unknown> };
  } | null;
  return {
    ok: false,
    status: response.status,
    code: body?.error?.code,
    message: body?.error?.message,
    details: body?.error?.details,
  };
}

function authHeaders(adminToken: string): HeadersInit {
  return {
    'content-type': 'application/json',
    authorization: `Bearer ${adminToken}`,
  };
}

export interface ListContestsFilter {
  readonly state?: string;
  readonly search?: string;
  readonly limit?: number;
  readonly offset?: number;
}

/** List Contests, filterable by state and searchable by name/ID, paginated server-side via
 * limit/offset (API-09 GET). */
export async function listContests(
  adminToken: string,
  filter: ListContestsFilter,
): Promise<ListContestsResult> {
  const params = new URLSearchParams();
  if (filter.state) params.set('state', filter.state);
  if (filter.search) params.set('search', filter.search);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  if (filter.offset !== undefined) params.set('offset', String(filter.offset));
  const query = params.toString();
  const response = await fetch(`${apiBase()}/api/admin/contests${query ? `?${query}` : ''}`, {
    method: 'GET',
    headers: authHeaders(adminToken),
  });
  if (response.ok) {
    return { ok: true, ...((await response.json()) as ListContestsBody) };
  }
  return failure(response);
}

/** Contest detail — full AdminContest plus live entryCount (API-09 GET). */
export async function getContest(adminToken: string, id: string): Promise<GetContestResult> {
  const response = await fetch(`${apiBase()}/api/admin/contests/${id}`, {
    method: 'GET',
    headers: authHeaders(adminToken),
  });
  if (response.ok) {
    return { ok: true, contest: (await response.json()) as AdminContestWithEntryCount };
  }
  return failure(response);
}

/** Finalise a Contest (API-09 POST); GL-16 guards surface as a 409 with error.details. */
export async function finaliseContest(
  adminToken: string,
  id: string,
  overrideSuspendedProps: boolean,
): Promise<FinaliseContestResult> {
  const response = await fetch(`${apiBase()}/api/admin/contests/${id}/finalise`, {
    method: 'POST',
    headers: authHeaders(adminToken),
    body: JSON.stringify({ overrideSuspendedProps }),
  });
  if (response.ok) {
    return { ok: true, ...((await response.json()) as FinaliseBody) };
  }
  return failure(response);
}

/**
 * Revert a finalised Scheduled Contest to draft (CAE-488 POST): discards the frozen snapshot so
 * everything is editable again. A `not_finalised`/`invalid_state` guard surfaces as a 409.
 */
export async function revertContest(adminToken: string, id: string): Promise<RevertContestResult> {
  const response = await fetch(`${apiBase()}/api/admin/contests/${id}/revert`, {
    method: 'POST',
    headers: authHeaders(adminToken),
    // The route takes no fields, but authHeaders sets application/json — send an empty object so
    // Fastify's JSON parser doesn't reject an empty body ("Body cannot be empty").
    body: JSON.stringify({}),
  });
  if (response.ok) {
    return { ok: true, contest: (await response.json()) as RevertBody };
  }
  return failure(response);
}

/** Create a draft Contest (API-09 POST) — basic info only; the editor owns curation. */
export async function createContest(
  adminToken: string,
  input: CreateContestInput,
): Promise<CreateContestResult> {
  const response = await fetch(`${apiBase()}/api/admin/contests`, {
    method: 'POST',
    headers: authHeaders(adminToken),
    body: JSON.stringify(input),
  });
  if (response.ok) {
    return { ok: true, contest: (await response.json()) as CreateContestResponseBody };
  }
  return failure(response);
}

/** Update draft Contest basic info and/or curation (API-09 PUT); partial — only sent fields change. */
export async function updateContest(
  adminToken: string,
  id: string,
  patch: UpdateContestPatch,
): Promise<UpdateContestResult> {
  const response = await fetch(`${apiBase()}/api/admin/contests/${id}`, {
    method: 'PUT',
    headers: authHeaders(adminToken),
    body: JSON.stringify(patch),
  });
  if (response.ok) {
    return { ok: true, ...((await response.json()) as UpdateContestResponseBody) };
  }
  return failure(response);
}

export interface ContestsClient {
  listContests(adminToken: string, filter: ListContestsFilter): Promise<ListContestsResult>;
  getContest(adminToken: string, id: string): Promise<GetContestResult>;
  finaliseContest(
    adminToken: string,
    id: string,
    overrideSuspendedProps: boolean,
  ): Promise<FinaliseContestResult>;
  revertContest(adminToken: string, id: string): Promise<RevertContestResult>;
  createContest(adminToken: string, input: CreateContestInput): Promise<CreateContestResult>;
  updateContest(
    adminToken: string,
    id: string,
    patch: UpdateContestPatch,
  ): Promise<UpdateContestResult>;
}

export const defaultContestsClient: ContestsClient = {
  listContests,
  getContest,
  finaliseContest,
  revertContest,
  createContest,
  updateContest,
};
